import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaHeart, FaShoppingCart, FaTrash } from 'react-icons/fa';
import useAuthStore from '../store/useAuthStore';
import useCartStore from '../store/useCartStore';
import useWishlistStore from '../store/useWishlistStore';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { getProductImage } from '../utils/imageHelpers';
import { formatPrice } from '../utils/formatPrice';

const Wishlist = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { addItem } = useCartStore();
  const { wishlist, loading, fetchWishlist, removeFromWishlist, clearWishlist } = useWishlistStore();

  useEffect(() => {
    fetchWishlist();
  }, [user]);

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await removeFromWishlist(productId);
    } catch (error) {
      toast.error(t('wishlist.messages.error'));
    }
  };

  const addToCart = async (product) => {
    try {
      await addItem(product._id, 1);
      toast.success(t('wishlist.messages.addToCartSuccess'));
    } catch (error) {
      toast.error(t('wishlist.messages.addToCartError'));
    }
  };

  const handleClearWishlist = async () => {
    const result = await Swal.fire({
      title: t('wishlist.clearConfirm.title'),
      text: t('wishlist.clearConfirm.text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('wishlist.clearConfirm.confirmButton'),
      cancelButtonText: t('wishlist.clearConfirm.cancelButton'),
      customClass: {
        confirmButton: 'swal2-delete-confirm mr-3',
        cancelButton: 'swal2-cancel-custom'
      },
      buttonsStyling: false
    });

    if (result.isConfirmed) {
      try {
        await clearWishlist();
      } catch (error) {
        toast.error(t('wishlist.messages.error'));
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const validProducts = wishlist?.filter(item => item.product) || [];

  if (validProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6">
        <FaHeart className="text-6xl text-gray-300 mb-6" />
        <h2 className="font-serif text-3xl font-light tracking-wide text-gray-900 mb-4">
          {t('wishlist.empty.title')}
        </h2>
        <p className="text-gray-600 font-light tracking-wide mb-8">
          {t('wishlist.empty.description')}
        </p>
        <Link to="/products" className="btn-primary">
          {t('wishlist.empty.button')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-wide text-center mb-4">
            {t('wishlist.title')}
          </h1>
          <p className="text-center text-gray-600 font-light tracking-wide">
            {t('wishlist.itemCount', { count: validProducts.length })}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Clear All Button */}
        {validProducts.length > 0 && (
          <div className="flex justify-end mb-8">
            <button
              onClick={handleClearWishlist}
              className="text-gray-600 hover:text-gray-900 transition text-sm tracking-wider uppercase font-light flex items-center gap-2"
            >
              <FaTrash />
              {t('wishlist.clearAll')}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {validProducts.map(({ product, addedAt }) => {
            if (!product) return null;

            const price = product.salePrice || product.price;
            const hasDiscount = product.salePrice && product.salePrice < product.price;

            return (
              <div key={product._id} className="group">
                {/* Product Image */}
                <Link to={`/products/${product.slug}`} className="block relative aspect-square bg-gray-50 mb-4 overflow-hidden">
                  {product.images && product.images.length > 0 ? (
                    <>
                      <img
                        src={getProductImage(product.images, 0)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:opacity-0 transition-opacity duration-500"
                      />
                      {product.images[1] && (
                        <img
                          src={getProductImage(product.images, 1)}
                          alt={product.name}
                          className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        />
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      {t('product.noImage')}
                    </div>
                  )}
                  {hasDiscount && (
                    <div className="absolute top-4 left-4">
                      <span className="badge badge-sale">
                        {t('product.discount').replace('{{rate}}', Math.round(((product.price - product.salePrice) / product.price) * 100))}
                      </span>
                    </div>
                  )}
                </Link>

                {/* Product Info */}
                <div className="space-y-3">
                  <Link
                    to={`/products/${product.slug}`}
                    className="font-light text-gray-900 hover:text-gray-600 transition line-clamp-2 block"
                  >
                    {product.name}
                  </Link>

                  {/* Price */}
                  <div className="flex items-center gap-2">
                    <span className="font-light text-gray-900">
                      {formatPrice(price)} ₺
                    </span>
                    {hasDiscount && (
                      <span className="text-sm text-gray-400 line-through font-light">
                        {formatPrice(product.price)} ₺
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0 || !product.isActive}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 py-2 text-xs"
                    >
                      <FaShoppingCart />
                      {t('wishlist.addToCart')}
                    </button>
                    <button
                      onClick={() => handleRemoveFromWishlist(product._id)}
                      className="px-4 border border-gray-200 hover:border-gray-400 transition"
                      title={t('wishlist.messages.removed')}
                    >
                      <FaTrash className="text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;

